/**
 * utils/compressImage.ts
 * Redimensiona y re-encodea una imagen en cliente vía canvas.
 *
 * Uso: avatares y fotos de paciente. Mantiene aspect ratio, recorta al lado largo
 * indicado y re-encodea como JPEG con la calidad dada.
 */

export interface CompressOptions {
  /** Máximo lado largo en píxeles. Por default 1024. */
  maxDimension?: number
  /** Calidad JPEG entre 0 y 1. Por default 0.85. */
  quality?: number
}

/**
 * Comprime una imagen. Si el resultado pesa más que el original (raro pero pasa
 * con PNGs ya optimizados), retorna el archivo original.
 */
export async function compressImage(file: File, opts: CompressOptions = {}): Promise<Blob> {
  const maxDimension = opts.maxDimension ?? 1024
  const quality      = opts.quality      ?? 0.85

  // `createImageBitmap` lanza InvalidStateError con archivos corruptos, con
  // extensión mentida o con formatos que el navegador no decodifica. Sin este
  // wrap el error viajaba crudo y la UI decía "Error al subir la foto" — cuando
  // no hubo subida: el archivo nunca se pudo leer (staging 2026-08-07).
  let bitmap: ImageBitmap
  try {
    bitmap = await createImageBitmap(file)
  } catch {
    throw new Error('No pudimos leer esa imagen. Puede estar dañada o en un formato que el navegador no abre. Prueba con otro archivo.')
  }
  const { width, height } = bitmap

  const scale = Math.min(1, maxDimension / Math.max(width, height))
  const targetW = Math.round(width  * scale)
  const targetH = Math.round(height * scale)

  const canvas = document.createElement('canvas')
  canvas.width  = targetW
  canvas.height = targetH

  const ctx = canvas.getContext('2d')
  if (!ctx) {
    bitmap.close()
    return file
  }

  ctx.drawImage(bitmap, 0, 0, targetW, targetH)
  bitmap.close()

  const blob: Blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      b => (b ? resolve(b) : reject(new Error('No se pudo comprimir la imagen'))),
      'image/jpeg',
      quality,
    )
  })

  return blob.size < file.size ? blob : file
}
